import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useState, useEffect, useMemo } from "react";
import { Minus, Plus, Trash2, UserPlus } from "lucide-react";
import { WorkforceCache } from "@/services/workforceCache";

interface TeamMember {
  name?: string;
  role: string;
  qty: number;
}

interface WorkforceTeamEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamData: {
    type?: "team";
    teamId?: string;
    teamName?: string;
    totalMembers?: number;
    members?: TeamMember[];
    employeeType?: "direta" | "terceirizada";
    observations?: string;
  };
  allMembers?: TeamMember[];
  onSave: (updatedTeam: any) => void;
}

export function WorkforceTeamEditModal({
  open,
  onOpenChange,
  teamData,
  allMembers = [],
  onSave
}: WorkforceTeamEditModalProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [newMemberRole, setNewMemberRole] = useState("");
  const [newMemberQty, setNewMemberQty] = useState(1);
  const [memberToDelete, setMemberToDelete] = useState<number | null>(null);
  
  // Funções reais: profissões do cadastro (WorkforceCache/Supabase + catálogo)
  const availableRoles = useMemo(
    () => WorkforceCache.getProfissoes().map((p) => p.nome),
    [open]
  );

  useEffect(() => {
    if (open && teamData.members) {
      setMembers([...teamData.members]);
    } else if (open) {
      setMembers([]);
    }
  }, [open, teamData.members]);

  const totalMembers = members.reduce((sum, member) => sum + member.qty, 0);

  const handleMemberQtyChange = (index: number, newQty: number) => {
    if (newQty >= 0) {
      const updatedMembers = [...members];
      updatedMembers[index] = { ...updatedMembers[index], qty: newQty };
      setMembers(updatedMembers);
    }
  };

  const handleDeleteMember = (index: number) => {
    const updatedMembers = members.filter((_, i) => i !== index);
    setMembers(updatedMembers);
    setMemberToDelete(null);
  };

  const handleAddMember = () => {
    if (!newMemberRole.trim()) return;

    // Check if member with same role already exists
    const existingMemberIndex = members.findIndex(
      member => member.role === newMemberRole && !member.name
    );

    if (existingMemberIndex >= 0) {
      // Merge quantities for same role
      const updatedMembers = [...members];
      updatedMembers[existingMemberIndex].qty += newMemberQty;
      setMembers(updatedMembers);
    } else {
      // Add new member
      const newMember: TeamMember = {
        role: newMemberRole,
        qty: newMemberQty
      };
      setMembers([...members, newMember]);
    }

    // Reset form
    setNewMemberRole("");
    setNewMemberQty(1);
  };

  const handleSave = () => {
    // Filter out members with 0 quantity
    const validMembers = members.filter(member => member.qty > 0);
    
    const updatedTeam = {
      ...teamData,
      members: validMembers,
      totalMembers: validMembers.reduce((sum, member) => sum + member.qty, 0)
    };

    onSave(updatedTeam);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setMembers([...teamData.members]);
    setNewMemberRole("");
    setNewMemberQty(1);
    onOpenChange(false);
  };

  const canRemoveTeam = totalMembers === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Editar Equipe: {teamData.teamName || "Equipe"}
          </DialogTitle>
          <DialogDescription>
            Total atual: {totalMembers} {totalMembers === 1 ? 'pessoa' : 'pessoas'} • 
            Mão de obra {teamData.employeeType === 'direta' ? 'própria' : 'terceirizada'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Members List */}
          <div className="space-y-4">
            <h4 className="font-medium">Membros da Equipe</h4>
            
            {members.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Profissão</TableHead>
                    <TableHead className="text-center">Quantidade</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {member.name || "—"}
                      </TableCell>
                      <TableCell>
                        {member.role}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleMemberQtyChange(index, member.qty - 1)}
                            disabled={member.qty <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={member.qty}
                            onChange={(e) => handleMemberQtyChange(index, parseInt(e.target.value) || 0)}
                            className="text-center w-16"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleMemberQtyChange(index, member.qty + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMemberToDelete(index)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover membro</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja remover {member.name || "este membro"} ({member.role}) da equipe?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => setMemberToDelete(null)}>
                                Cancelar
                              </AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteMember(index)}>
                                Remover
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum membro na equipe</p>
                <p className="text-sm">Adicione membros usando o formulário abaixo</p>
              </div>
            )}
          </div>

          {/* Add New Member */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-4">Adicionar Novo Membro</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newRole">Profissão</Label>
                <Select value={newMemberRole} onValueChange={setNewMemberRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar profissão" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newQty">Quantidade</Label>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewMemberQty(Math.max(1, newMemberQty - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="newQty"
                    type="number"
                    min="1"
                    value={newMemberQty}
                    onChange={(e) => setNewMemberQty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="text-center w-20"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewMemberQty(newMemberQty + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={handleAddMember}
                  disabled={!newMemberRole.trim()}
                  className="w-full"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          {canRemoveTeam && (
            <Button variant="destructive" onClick={() => onSave(null)}>
              Remover Equipe
            </Button>
          )}
          <Button onClick={handleSave}>
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}